import { X } from 'lucide-react'
import { useState } from 'react'

import {
  useCreateTeam,
  useUpdateTeam,
  type Team,
  type CreateTeamInput,
  type UpdateTeamInput,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface TeamFormProps {
  team?: Team
  onClose: () => void
  onSuccess?: () => void
}

/**
 * 团队表单组件（创建/编辑）
 */
export function TeamForm({ team, onClose, onSuccess }: TeamFormProps) {
  const isEdit = !!team
  const createMutation = useCreateTeam()
  const updateMutation = useUpdateTeam()

  const [formData, setFormData] = useState({
    name: team?.name || '',
    description: team?.description || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = '请输入团队名称'
    } else if (formData.name.length > 100) {
      newErrors.name = '团队名称不能超过100个字符'
    }
    if (formData.description && formData.description.length > 2000) {
      newErrors.description = '团队描述不能超过2000个字符'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      return
    }

    if (isEdit) {
      const data: UpdateTeamInput = {
        name: formData.name,
        description: formData.description || undefined,
      }
      updateMutation.mutate(
        { id: team.id, data },
        {
          onSuccess: () => {
            onSuccess?.()
            onClose()
          },
        }
      )
    } else {
      const data: CreateTeamInput = {
        name: formData.name,
        description: formData.description || undefined,
      }
      createMutation.mutate(data, {
        onSuccess: () => {
          onSuccess?.()
          onClose()
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const mutationError = createMutation.error || updateMutation.error

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {isEdit ? '编辑团队' : '新建团队'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* 团队名称 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                团队名称 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入团队名称"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* 团队描述 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium">团队描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入团队描述"
                rows={3}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.description && 'border-destructive'
                )}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-destructive">{errors.description}</p>
              )}
            </div>

            {/* 错误提示 */}
            {mutationError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {mutationError.message}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEdit ? '保存' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
